import { CARD_SPEC } from './cardSpec';
import {
  assetUrl,
  loadImage,
  ensureFont,
  KNAME_FONT,
  getAlphaBbox,
  rasterize,
  toNaturalCanvas,
} from './knameAssets';
import { ZODIAC_KO, STAR_SIGN_KO } from './derive';

const CHARACTER_OFFSET_X = {
  snake: 70,
  horse: 60,
  goat: 46,
  tiger: 50,
  dragon: 34,
  monkey: 50,
  rooster: 30,
  dog: 40,
  pig: 20,
  rat: 40,
  ox: 20,
};

/** TODO: 워터마크 에셋 나오기 전 임시 — 나오면 이미지 드로우로 교체 */
const drawTempWatermark = (ctx) => {
  ctx.save();
  ctx.globalAlpha = 0.15;
  ctx.font = `150px ${KNAME_FONT}`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.translate(600, 900);
  ctx.rotate(-Math.PI / 6);
  for (let y = -800; y <= 800; y += 260) {
    ctx.fillText('Koready', 0, y);
  }
  ctx.restore();
};

/** 안전영역 안에서 폰트 자동 축소 후 중앙 렌더 */
const drawFittedText = (ctx, text, box, { min, max, color }) => {
  let size = max;
  ctx.font = `${size}px ${KNAME_FONT}`;
  while (ctx.measureText(text).width > box.w && size > min) {
    size -= 2;
    ctx.font = `${size}px ${KNAME_FONT}`;
  }
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, box.x + box.w / 2, box.y + box.h / 2);
};

/** 의미칸 줄바꿈 렌더 — 가로/세로 정렬 지원 */
const drawWrappedText = (
  ctx,
  text,
  box,
  { fontSize, lineHeight, color, align = 'left', valign = 'top' },
) => {
  ctx.font = `${fontSize}px ${KNAME_FONT}`;
  const lineH = fontSize * lineHeight;

  const lines = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(' ')) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > box.w && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
  }

  const totalH = lines.length * lineH;

  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = 'top';
  const drawX = align === 'center' ? box.x + box.w / 2 : box.x;

  let y =
    valign === 'center' ? box.y + Math.max(0, (box.h - totalH) / 2) : box.y;

  for (const l of lines) {
    if (y + lineH > box.y + box.h + 1) return;
    ctx.fillText(l, drawX, y);
    y += lineH;
  }
};

const makeCanvas = () => {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_SPEC.canvas.w;
  canvas.height = CARD_SPEC.canvas.h;
  return canvas;
};

/**
 * 앞장 렌더 — 배경(별자리) → 캐릭터(간지) → 명패 → 이름 → (워터마크)
 * @returns HTMLCanvasElement (1200×1800)
 */
export async function renderFrontCard({
  backgroundId,
  zodiacId,
  hangul,
  watermark = true,
  fitToSpec = true,
}) {
  await ensureFont();
  const [bg, ch, plate] = await Promise.all([
    loadImage(assetUrl.background(backgroundId)),
    loadImage(assetUrl.character(zodiacId)),
    loadImage(assetUrl.plate()),
  ]);

  const canvas = makeCanvas();
  const ctx = canvas.getContext('2d');
  const S = CARD_SPEC.front;

  // ① 배경
  ctx.drawImage(bg, 0, 0, CARD_SPEC.canvas.w, CARD_SPEC.canvas.h);

  // ② 캐릭터 — 원본 비율 유지 → 알파박스를 fit에 contain → 하단중앙
  const chCanvas = toNaturalCanvas(ch);
  const bbox = getAlphaBbox(chCanvas, `${zodiacId}@natural`);
  const scale = Math.min(
    S.character.fit.w / bbox.w,
    S.character.fit.h / bbox.h,
  );
  const dw = bbox.w * scale;
  const dh = bbox.h * scale;
  const offsetX = CHARACTER_OFFSET_X[zodiacId] ?? 0;
  ctx.drawImage(
    chCanvas,
    bbox.x,
    bbox.y,
    bbox.w,
    bbox.h,
    S.character.centerX - dw / 2 + offsetX,
    S.character.baselineY - dh,
    dw,
    dh,
  );

  // ④ 명패 (캐릭터 발밑을 덮는 게 의도)
  ctx.drawImage(plate, S.plate.x, S.plate.y, S.plate.w, S.plate.h);

  // ④ 명패 → ⑤ 이름 → ⑥ 워터마크 (기존 동일)
  ctx.drawImage(plate, S.plate.x, S.plate.y, S.plate.w, S.plate.h);
  drawFittedText(ctx, hangul, S.nameSafe, S.nameFont);
  if (watermark) drawTempWatermark(ctx);

  return canvas;
}

/**
 * 뒷장 렌더 — 베이스 → 명패 → 이름 → 의미(폴백 체인) → footer → (워터마크)
 */
export async function renderBackCard({
  name,
  zodiacId,
  backgroundId,
  watermark = true,
}) {
  await ensureFont();
  const base = await loadImage(assetUrl.backBase());

  const canvas = makeCanvas();
  const ctx = canvas.getContext('2d');
  const S = CARD_SPEC.back;

  ctx.drawImage(base, 0, 0, CARD_SPEC.canvas.w, CARD_SPEC.canvas.h);

  drawFittedText(ctx, name.hangul, S.name, S.nameFont);

  const intro = `${ZODIAC_KO[zodiacId]}와 ${STAR_SIGN_KO[backgroundId]}의 기운을\n 타고 났네요!`;
  const meaning = name.meaning_ko || '';
  drawWrappedText(ctx, meaning ? `${intro}\n${meaning}` : intro, S.meaning, {
    ...S.meaning,
    align: 'center',
    valign: 'center',
  });

  // footer
  ctx.font = `${S.footer.fontSize}px ${KNAME_FONT}`;
  ctx.fillStyle = S.footer.color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(
    S.footer.text,
    S.footer.x + S.footer.w / 2,
    S.footer.y + S.footer.h / 2,
  );

  if (watermark) drawTempWatermark(ctx);

  return canvas;
}

/** 미리보기용 다운스케일 → dataURL (480×720) */
export const toPreviewDataUrl = (canvas) => {
  const p = document.createElement('canvas');
  p.width = CARD_SPEC.preview.w;
  p.height = CARD_SPEC.preview.h;
  p.getContext('2d').drawImage(canvas, 0, 0, p.width, p.height);
  return p.toDataURL('image/png');
};
